import React from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { gql } from '@apollo/client';
import { useQueryCoordinate } from '../../clients/graphql/graphql.hooks';
import { Box, Paper, Typography, TextField, MenuItem, IconButton, Button, Chip, Skeleton, Drawer, Grid, Modal, Autocomplete } from '@mui/material';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { ArrowBack, ArrowForward, ContentCopy as ContentCopyIcon } from '@mui/icons-material';
import { BROKER_NAME } from '../../shared/portfolio-types';

const GET = gql`
  query BalancingStateMetricsConnectionGet($input: BalancingStateMetricsConnectionGetInput, $balancingStatesInput: BalancingStatesInput) {
    balancingStateMetricsConnectionGet(input: $input) {
      workRequiredDistribution { name count percentage }
      balancingStates(input: $balancingStatesInput) {
        total
        balancingStates {
          autoPilotSettingsKey
          state
          stateType
          workRequiredAttributionState
          workRequiredAttributionStateType
          workflowId
          createdAt
          data
          autoPilotBalancingActivities {
            createdAt
            positionMarketValueAmount
            slaveCashPositionAmount
            distanceToTargetPositions
            data
            balanceDecisions {
              symbol
              orderSide
              quantity
              price
              isExecuteOrderDecision
              kind
              positionType
              positionEffect
              reason
            }
            autoPilotOrders {
              symbol
              orderSide
              quantity
              orderStatus
              orderState
              failureReason
            }
          }
        }
      }
    }
  }
`;
const MARKETPLACE_SEARCH = gql`
  query MarketPlaceSearch($input: MarketPlaceSearchInput!) {
    marketPlaceSearch(input: $input) {
      teams { teamKey title }
      portfolios {
        portfolioKey
        autoPilotMasterPortfolio { title team { title } }
        user { firstName lastName username }
      }
    }
  }
`;

const WORK_COLORS: Record<string, string> = {
  'No work required': '#10b981',
  'Platform Issue': '#f59e0b',
  'User Issue': '#3b82f6',
  'Broker Issue': '#ef4444',
};

const WORK_NAME_TO_CODE: Record<string, number> = {
  'No work required': 0,
  'Platform Issue': 1,
  'User Issue': 2,
  'Broker Issue': 3,
};

const STATE_CODE_TO_NAME: Record<number, string> = {
  0: 'TERMINAL',
  1: 'IN_PROGRESS',
  2: 'FAILED',
};
const WRAS_CODE_TO_NAME: Record<number, string> = { 0: 'NO_WORK_REQUIRED', 1: 'PLATFORM', 2: 'USER', 3: 'BROKER' };

export const DrilldownBalancingStatesPage: React.FC = () => {
  const [sp, setSp] = useSearchParams();
  const navigate = useNavigate();
  const [teamKey, setTeamKey] = React.useState<string>(sp.get('teamKey') || '');
  const [selectedTeamTitle, setSelectedTeamTitle] = React.useState<string>('');
  const [portfolioKey, setPortfolioKey] = React.useState<string>(sp.get('portfolioKey') || '');
  const [brokerName, setBrokerName] = React.useState<string>(sp.get('brokerName') || '');
  const [state, setState] = React.useState<number>(sp.get('state') ? Number(sp.get('state')) : 0);
  const [limit, setLimit] = React.useState<number>(25);
  const [offset, setOffset] = React.useState<number>(0);
  const [selectedWork, setSelectedWork] = React.useState<number | undefined>(sp.get('work') ? Number(sp.get('work')) : undefined);

  React.useEffect(() => {
    const params: Record<string, string> = { state: String(state) };
    if (teamKey) params.teamKey = teamKey;
    if (portfolioKey) params.portfolioKey = portfolioKey;
    if (brokerName) params.brokerName = brokerName as string;
    if (typeof selectedWork === 'number') params.work = String(selectedWork);
    setSp(params, { replace: true });
  }, [teamKey, portfolioKey, brokerName, state, selectedWork, setSp]);

  const variables = React.useMemo(() => {
    const input: any = { state };
    if (teamKey) input.teamKey = Number(teamKey);
    if (portfolioKey) input.portfolioKey = Number(portfolioKey);
    if (brokerName) input.brokerName = brokerName;
    // IMPORTANT: Keep distribution unfiltered by work type so all slices remain visible
    const balancingStatesInput: any = { limit, offset, state };
    if (typeof selectedWork === 'number') balancingStatesInput.workRequiredAttributionState = selectedWork;
    return { input, balancingStatesInput };
  }, [teamKey, portfolioKey, brokerName, state, selectedWork, limit, offset]);

  // Team search
  const [teamOpen, setTeamOpen] = React.useState(false);
  const [teamSearchValue, setTeamSearchValue] = React.useState('');
  const { data: teamSearchData } = useQueryCoordinate(MARKETPLACE_SEARCH, {
    variables: { input: { searchValue: teamSearchValue || '', limitTeams: 8, offsetTeams: 0, limitPortfolios: 0, offsetPortfolios: 0 } },
    skip: !teamOpen,
  });

  // Portfolio search
  const [portfolioOpen, setPortfolioOpen] = React.useState(false);
  const [portfolioSearchValue, setPortfolioSearchValue] = React.useState('');
  const { data: portfolioSearchData } = useQueryCoordinate(MARKETPLACE_SEARCH, {
    variables: { input: { searchValue: portfolioSearchValue || '', limitTeams: 0, offsetTeams: 0, limitPortfolios: 10, offsetPortfolios: 0 } },
    skip: !portfolioOpen,
  });

  const { data, loading, error } = useQueryCoordinate(GET, { variables, fetchPolicy: 'cache-and-network' });
  const wrd = data?.balancingStateMetricsConnectionGet?.workRequiredDistribution || [];
  const resp = data?.balancingStateMetricsConnectionGet?.balancingStates;
  const items = resp?.balancingStates || [];
  const total = resp?.total || 0;

  const canPrev = offset > 0;
  const canNext = offset + limit < total;

  // Drawer & modals (adapted from BalancingStatesPage)
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [selectedStateRow, setSelectedStateRow] = React.useState<any>(null);
  const [selectedActivity, setSelectedActivity] = React.useState<any>(null);
  const [balanceDecisionsModalOpen, setBalanceDecisionsModalOpen] = React.useState(false);
  const [activityDataModalOpen, setActivityDataModalOpen] = React.useState(false);
  const [balancingStateDataModalOpen, setBalancingStateDataModalOpen] = React.useState(false);
  const [copiedActivity, setCopiedActivity] = React.useState(false);
  const [copiedState, setCopiedState] = React.useState(false);

  const handleRowClick = (row: any) => { setSelectedStateRow(row); setDrawerOpen(true); };
  const closeDrawer = () => { setDrawerOpen(false); setSelectedStateRow(null); };
  const openDecisions = (activity: any) => { setSelectedActivity(activity); setBalanceDecisionsModalOpen(true); };
  const closeDecisions = () => { setBalanceDecisionsModalOpen(false); setSelectedActivity(null); };
  const openActivityData = (activity: any) => { setSelectedActivity(activity); setActivityDataModalOpen(true); };
  const closeActivityData = () => { setActivityDataModalOpen(false); setSelectedActivity(null); };
  const openStateData = () => setBalancingStateDataModalOpen(true);
  const closeStateData = () => setBalancingStateDataModalOpen(false);

  const handleCopyActivityData = async () => {
    if (!selectedActivity?.data) return;
    try {
      const jsonString = JSON.stringify(selectedActivity.data, null, 2).replace(/\n+$/, '');
      await navigator.clipboard.writeText(jsonString);
      setCopiedActivity(true);
      setTimeout(() => setCopiedActivity(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleCopyStateData = async () => {
    if (!selectedStateRow?.data) return;
    try {
      const jsonString = JSON.stringify(selectedStateRow.data, null, 2).replace(/\n+$/, '');
      await navigator.clipboard.writeText(jsonString);
      setCopiedState(true);
      setTimeout(() => setCopiedState(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>Balancing States Drilldown</Typography>
        <Box>
          <Button variant="outlined" startIcon={<ArrowBack />} onClick={() => {
            const params: Record<string,string> = {};
            if (teamKey) params.teamKey = teamKey;
            if (portfolioKey) params.portfolioKey = portfolioKey;
            if (brokerName) params.brokerName = brokerName as string;
            const qs = new URLSearchParams(params).toString();
            navigate(`/balancing-states-metrics${qs ? `?${qs}` : ''}`);
          }}>
            Back to Overview
          </Button>
        </Box>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Autocomplete
            freeSolo open={teamOpen} onOpen={()=>{setTeamOpen(true); setTeamSearchValue('');}} onClose={()=>setTeamOpen(false)}
            options={(teamSearchData?.marketPlaceSearch?.teams || []).map((t:any)=>({label:`${t.title} (#${t.teamKey})`, teamKey:String(t.teamKey), title:t.title}))}
            inputValue={teamSearchValue}
            onInputChange={(_,v)=>setTeamSearchValue(v)}
            onChange={(_,val:any)=>{
              if (val && typeof val === 'object') { setTeamKey(val.teamKey); setSelectedTeamTitle(val.title||''); }
              else if (typeof val === 'string') { setTeamKey(val.trim()); }
            }}
            renderInput={(params)=>(<TextField {...params} label="Pilot Team" size="small" placeholder="Search by name or enter key" />)}
          />
          <Autocomplete
            freeSolo open={portfolioOpen} onOpen={()=>{setPortfolioOpen(true); setPortfolioSearchValue('');}} onClose={()=>setPortfolioOpen(false)}
            options={(() => {
              const all = (portfolioSearchData?.marketPlaceSearch?.portfolios || []).map((p:any)=>({label:`${p.autoPilotMasterPortfolio?.title||'Portfolio'} (#${p.portfolioKey})`, portfolioKey:String(p.portfolioKey), teamTitle:p.autoPilotMasterPortfolio?.team?.title||''}));
              if (selectedTeamTitle) return all.filter((o:any)=> o.teamTitle === selectedTeamTitle);
              return all;
            })()}
            inputValue={portfolioSearchValue}
            onInputChange={(_,v)=>setPortfolioSearchValue(v)}
            onChange={(_,val:any)=>{
              if (val && typeof val === 'object') { setPortfolioKey(val.portfolioKey); }
              else if (typeof val === 'string') { setPortfolioKey(val.trim()); }
            }}
            renderInput={(params)=>(<TextField {...params} label="Master Portfolio" size="small" placeholder="Search by name or enter key" />)}
          />
          <TextField select label="Broker" size="small" value={brokerName} onChange={e=>setBrokerName(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            {Object.keys(BROKER_NAME).map(k => <MenuItem key={k} value={(BROKER_NAME as any)[k]}>{k}</MenuItem>)}
          </TextField>
          <TextField select label="Rows" size="small" value={limit} onChange={e=>{setLimit(Number(e.target.value)); setOffset(0);}}>
            {[25,50,100].map(n=> <MenuItem key={n} value={n}>{n}</MenuItem>)}
          </TextField>
          <Box sx={{ ml: 'auto' }}>
            <IconButton disabled={!canPrev} onClick={()=> setOffset(Math.max(0, offset - limit))}><ArrowBack /></IconButton>
            <IconButton disabled={!canNext} onClick={()=> setOffset(offset + limit)}><ArrowForward /></IconButton>
            <Typography variant="body2" sx={{ ml: 1, display: 'inline-block' }}>{total ? `${Math.min(offset+1,total)}-${Math.min(offset+limit,total)} of ${total}`: '0 of 0'}</Typography>
          </Box>
        </Box>
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>Work Required Distribution</Typography>
        <Box sx={{ height: 320 }}>
          {loading && wrd.length === 0 ? (
            <Skeleton variant="rectangular" height={300} />
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={wrd} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={100}
                     isAnimationActive={false} animationDuration={0} animationBegin={0}
                     onClick={(e:any)=>{
                       const code = WORK_NAME_TO_CODE[e?.name];
                       if (typeof code === 'number') {
                         setSelectedWork(prev => prev === code ? undefined : code);
                         setOffset(0);
                       }
                     }}>
                  {wrd.map((entry: any, idx: number) => {
                    const code = WORK_NAME_TO_CODE[entry.name];
                    const isSelected = typeof selectedWork === 'number' && selectedWork === code;
                    return (
                      <Cell key={idx} fill={WORK_COLORS[entry.name] || '#94a3b8'} stroke={isSelected ? '#ffffff' : 'none'} strokeWidth={isSelected ? 3 : 0}
                        style={{ cursor: 'pointer', filter: isSelected ? 'brightness(1.2)' : 'none', opacity: typeof selectedWork === 'number' && !isSelected ? 0.5 : 1 }} />
                    );
                  })}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Box>
      </Paper>

      <Paper>
        <Box sx={{ p: 2 }}>
          <Typography variant="h6">Balancing States</Typography>
        </Box>
        <Box sx={{ px: 2, pb: 2, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'rgba(0,0,0,0.04)' }}>
                <th style={{ textAlign: 'left', padding: '8px' }}>Pilot Details</th>
                <th style={{ textAlign: 'left', padding: '8px' }}>State</th>
                <th style={{ textAlign: 'left', padding: '8px' }}>Work Required</th>
                <th style={{ textAlign: 'left', padding: '8px' }}>Workflow</th>
                <th style={{ textAlign: 'left', padding: '8px' }}>Created At</th>
                <th style={{ textAlign: 'left', padding: '8px' }}>Details</th>
              </tr>
            </thead>
            <tbody>
              {loading && items.length === 0 ? (
                [...Array(5)].map((_, idx) => (
                  <tr key={idx}>
                    <td style={{ padding: '8px' }}><Skeleton width={80} /></td>
                    <td style={{ padding: '8px' }}><Skeleton width={100} /></td>
                    <td style={{ padding: '8px' }}><Skeleton width={140} /></td>
                    <td style={{ padding: '8px' }}><Skeleton width={60} /></td>
                    <td style={{ padding: '8px' }}><Skeleton width={160} /></td>
                    <td style={{ padding: '8px' }}><Skeleton width={90} /></td>
                  </tr>
                ))
              ) : error ? (
                <tr><td colSpan={6} style={{ padding: '16px', color: '#ef4444' }}>Error: {error.message}</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={6} style={{ padding: '24px' }}><Box sx={{ textAlign: 'center', color: 'text.secondary' }}>No results</Box></td></tr>
              ) : (
                items.map((s: any, i: number) => {
                  const stateLabel: string = s.stateType || STATE_CODE_TO_NAME[s.state] || String(s.state);
                  const wrasLabel: string = s.workRequiredAttributionStateType || WRAS_CODE_TO_NAME[s.workRequiredAttributionState] || String(s.workRequiredAttributionState);
                  return (
                    <tr key={i} style={{ borderBottom: '1px solid rgba(0,0,0,0.06)', cursor: 'pointer' }} onClick={() => handleRowClick(s)}>
                      <td style={{ padding: '8px' }}>
                        <Button size="small" variant="outlined" component={Link} to={`/pilot-details/${s.autoPilotSettingsKey}`}
                                onClick={(e)=> e.stopPropagation()}>
                          View Pilot Details
                        </Button>
                      </td>
                      <td style={{ padding: '8px' }}><Chip size="small" label={stateLabel} /></td>
                      <td style={{ padding: '8px' }}><Chip size="small" label={wrasLabel} /></td>
                      <td style={{ padding: '8px' }}>{s.workflowId ? (<Button component={Link} to={`/developer/workflows/BalancingLegacyWorkflow/executions/${s.workflowId}`} size="small" variant="outlined">View</Button>) : '-'}</td>
                      <td style={{ padding: '8px' }}>{new Date(s.createdAt).toLocaleString()}</td>
                      <td style={{ padding: '8px' }}>
                        <Button size="small" variant="outlined" onClick={(e)=>{ e.stopPropagation(); handleRowClick(s); }}>Details</Button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </Box>
      </Paper>

      <Drawer anchor="right" open={drawerOpen} onClose={closeDrawer}
        PaperProps={{ sx: { width: '70%', boxSizing: 'border-box' } }}>
        {selectedStateRow && (
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 3, pb: 2, borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
              <Box>
                <Typography variant="h5" fontWeight={600}>Balancing State Details</Typography>
                <Typography variant="body2">{(selectedStateRow.stateType || STATE_CODE_TO_NAME[selectedStateRow.state] || selectedStateRow.state)} • {new Date(selectedStateRow.createdAt).toLocaleDateString()}</Typography>
              </Box>
              <Button onClick={closeDrawer}>Close</Button>
            </Box>
            <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
              <Box sx={{ mb: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={6}><Typography variant="body2"><b>Workflow ID:</b> {selectedStateRow.workflowId || 'N/A'}</Typography></Grid>
                  <Grid item xs={6}><Typography variant="body2"><b>Created:</b> {new Date(selectedStateRow.createdAt).toLocaleString()}</Typography></Grid>
                </Grid>
                {selectedStateRow.data && (
                  <Box sx={{ mt: 1 }}>
                    <Button variant="outlined" size="small" onClick={openStateData}>View State Data</Button>
                  </Box>
                )}
              </Box>

              {selectedStateRow.autoPilotBalancingActivities && selectedStateRow.autoPilotBalancingActivities.length > 0 ? (
                <Box>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>BALANCING ACTIVITIES ({selectedStateRow.autoPilotBalancingActivities.length})</Typography>
                  {selectedStateRow.autoPilotBalancingActivities.map((activity: any, index: number) => (
                    <Box key={index} sx={{ border: '1px solid rgba(0,0,0,0.08)', borderRadius: 2, p: 2, mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="body2" fontWeight={600}>Activity {index + 1}</Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          {activity.balanceDecisions && activity.balanceDecisions.length > 0 && (
                            <Button variant="outlined" size="small" onClick={(e)=>{e.stopPropagation(); openDecisions(activity);}}>View Decisions ({activity.balanceDecisions.length})</Button>
                          )}
                          {activity.data && (
                            <Button variant="outlined" size="small" onClick={(e)=>{e.stopPropagation(); openActivityData(activity);}}>View Data</Button>
                          )}
                        </Box>
                      </Box>
                      <Grid container spacing={2}>
                        <Grid item xs={6}><Typography variant="body2"><b>Position MV:</b> {activity.positionMarketValueAmount ?? 'N/A'}</Typography></Grid>
                        <Grid item xs={6}><Typography variant="body2"><b>Cash:</b> {activity.slaveCashPositionAmount ?? 'N/A'}</Typography></Grid>
                        <Grid item xs={6}><Typography variant="body2"><b>Distance To Target:</b> {activity.distanceToTargetPositions ?? 'N/A'}</Typography></Grid>
                        <Grid item xs={6}><Typography variant="body2"><b>Created:</b> {new Date(activity.createdAt).toLocaleString()}</Typography></Grid>
                      </Grid>
                      {activity.autoPilotOrders && activity.autoPilotOrders.length > 0 && (
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>Orders ({activity.autoPilotOrders.length})</Typography>
                          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                              <tr style={{ background: 'rgba(0,0,0,0.04)' }}>
                                <th style={{ textAlign: 'left', padding: '6px' }}>Symbol</th>
                                <th style={{ textAlign: 'left', padding: '6px' }}>Side</th>
                                <th style={{ textAlign: 'left', padding: '6px' }}>Qty</th>
                                <th style={{ textAlign: 'left', padding: '6px' }}>Status</th>
                                <th style={{ textAlign: 'left', padding: '6px' }}>State</th>
                                <th style={{ textAlign: 'left', padding: '6px' }}>Reason</th>
                              </tr>
                            </thead>
                            <tbody>
                              {activity.autoPilotOrders.map((o:any, oi:number) => (
                                <tr key={oi}>
                                  <td style={{ padding: '6px' }}>{o.symbol}</td>
                                  <td style={{ padding: '6px' }}>{o.orderSide}</td>
                                  <td style={{ padding: '6px' }}>{o.quantity}</td>
                                  <td style={{ padding: '6px' }}>{o.orderStatus}</td>
                                  <td style={{ padding: '6px' }}>{o.orderState || '-'}</td>
                                  <td style={{ padding: '6px' }}>{o.failureReason || '-'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </Box>
                      )}
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" sx={{ fontStyle: 'italic' }}>No balancing activities found for this state.</Typography>
              )}
            </Box>
          </Box>
        )}
      </Drawer>

      <Modal open={balanceDecisionsModalOpen} onClose={closeDecisions}>
        <Box sx={{ p: 2, background: 'rgba(0,0,0,0.9)', color: '#fff', width: '90%', maxWidth: 1100, maxHeight: '90vh', overflow: 'auto', margin: '5vh auto', borderRadius: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Balance Decisions</Typography>
          {selectedActivity?.balanceDecisions?.map((d:any, i:number)=>(
            <Box key={i} sx={{ mb: 1, p: 1, border: '1px solid rgba(255,255,255,0.1)', borderRadius: 1 }}>
              <Typography variant="body2"><b>{d.orderSide}</b> {d.symbol} • qty {d.quantity} @ {d.price}</Typography>
              <Typography variant="body2">{d.kind} • {d.positionType} • {d.positionEffect}</Typography>
              <Typography variant="body2">{d.isExecuteOrderDecision ? 'Execute' : 'No Execute'}</Typography>
              <Typography variant="body2">{d.reason || '-'}</Typography>
            </Box>
          ))}
          <Box sx={{ textAlign: 'right', mt: 2 }}><Button onClick={closeDecisions} variant="outlined">Close</Button></Box>
        </Box>
      </Modal>

      <Modal open={activityDataModalOpen} onClose={closeActivityData}>
        <Box sx={{ p: 2, background: 'rgba(0,0,0,0.9)', color: '#fff', width: '90%', maxWidth: 1100, maxHeight: '90vh', overflow: 'auto', margin: '5vh auto', borderRadius: 2, position: 'relative' }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Activity Data (JSON)</Typography>
          <IconButton
            size="small"
            onClick={handleCopyActivityData}
            sx={{
              position: 'absolute',
              top: '1rem',
              right: '1rem',
              color: copiedActivity ? '#4caf50' : '#fff',
              backgroundColor: 'rgba(255,255,255,0.1)',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.2)'
              }
            }}
            title={copiedActivity ? 'Copied!' : 'Copy to clipboard'}
          >
            <ContentCopyIcon fontSize="small" />
          </IconButton>
          <pre style={{ whiteSpace: 'pre-wrap' }}>{selectedActivity?.data ? JSON.stringify(selectedActivity.data, null, 2).replace(/\n+$/, '') : 'No data'}</pre>
          <Box sx={{ textAlign: 'right', mt: 2 }}><Button onClick={closeActivityData} variant="outlined">Close</Button></Box>
        </Box>
      </Modal>

      <Modal open={balancingStateDataModalOpen} onClose={closeStateData}>
        <Box sx={{ p: 2, background: 'rgba(0,0,0,0.9)', color: '#fff', width: '90%', maxWidth: 1100, maxHeight: '90vh', overflow: 'auto', margin: '5vh auto', borderRadius: 2, position: 'relative' }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Balancing State Data (JSON)</Typography>
          <IconButton
            size="small"
            onClick={handleCopyStateData}
            sx={{
              position: 'absolute',
              top: '1rem',
              right: '1rem',
              color: copiedState ? '#4caf50' : '#fff',
              backgroundColor: 'rgba(255,255,255,0.1)',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.2)'
              }
            }}
            title={copiedState ? 'Copied!' : 'Copy to clipboard'}
          >
            <ContentCopyIcon fontSize="small" />
          </IconButton>
          <pre style={{ whiteSpace: 'pre-wrap' }}>{selectedStateRow?.data ? JSON.stringify(selectedStateRow.data, null, 2).replace(/\n+$/, '') : 'No data'}</pre>
          <Box sx={{ textAlign: 'right', mt: 2 }}><Button onClick={closeStateData} variant="outlined">Close</Button></Box>
        </Box>
      </Modal>
    </Box>
  );
};


